// index.ts — entry point for the object-card bundle (IIFE → includes/js/dist/
// object-card-bundle.js). Exposes window.renderObjectCard + the mount scanner
// window.ftMountObjectCards, the client-side port of the DISPLAY region of
// options/tables/table_item_object_inc.php.
//
// Fed by the PHP mount ft_object_card_render_mount() (object_card_mount.php), which emits
//   <div class="ft-oc-ts-card">
//     <script type="application/json" class="ft-oc-ts-blob"> … </script>
//     <script type="application/json" class="ft-oc-ts-names"> … </script>
//   </div>
// The property-LIST sub-region is delegated back to the list-display bundle
// (window.renderItemListDisplay) into the `.ft-oc-property-list` node this render emits.
//
// Render-time helpers (iconMap / escapeHtml / ucwordsAndSpace / BASE_URL) come from
// includes/js/core-helpers.js (loaded FIRST in the footer). Load order mirrors
// list-display: this bundle loads right after core-helpers + item-arr-readers.

import { compileFetchedEntity } from '../shared/compile-fetched-entity';
import { holdPendingEntityCard } from './entity-card-state';
import { registerInventoryTotalElement } from '../janus-engine/display/inventory-totals-21';
registerInventoryTotalElement(resolveInventoryDisplayRow);
import { buildNamesMapFromBlob } from './build-names-map';
import { itemsCol, layerEntriesOf, scalarLayerSlot } from '../shared/array-types';
import { buildObjectCardHtml } from './render-object-card';
import { previewSpawnDraft } from '../shared/spawn-browser-bridge';
import { ftWireContainerLootGen } from './container-loot-gen';
import { ftHydrateObjectCardIslands } from './islands';
import { hydrateEncounterLinkZones } from '../shared/encounter-link-zone';
import { inventoryInstanceRootRequest, paintInventoryInstanceRoot, releaseInventoryInstanceClaim } from './inventory-instance-root';
import { claimInventoryInstance } from '../shared/inventory-instance-root';
import type { ReferencedNames } from './types';
// Side effect: registers window.ftRenderItemFromCache (the instant cache-first openItemInfo path).
import './render-from-cache';
// Side effect: registers the open-side-info subscribe glue (ftSetOpenSideInfo / ftRerenderOpenSideInfo /
// ftCollectInventoryIds) so websocket updates can re-render the open drawer card in place.
import './open-side-info';
import './world-container-window';
// Side effect: registers window.ftRenderCreatureCardFromCache (instant unified-creature-card render
// for live encounter creatures — the GM-viewer dblclick + side-info creature open, zero round-trip).
import './render-creature-card';
// Side effect: registers window.ftRenderPlayerCardFromCache + the window.ftRenderCasterCardFromCache
// dispatcher (creature-or-PC) that template.js calls at both open sites.
import './render-player-card';
// Side effect: registers window.ftCraftWipAction (Work/Stop buttons on the crafting Phase-3
// craft_controls island — server-rendered fragment, so it calls the global by name).
import './craft-controls';
import './throw-object';
// Side effect: hurl wheel entries and the named refusal before object picking or casting.
import './hurl-object';
import './object-manipulation';
import './scene-radial';
// Side effect: single-click an item in your FT3D inventory windows → the Drop / Throw wheel (owner 2026-09-16).
import './bag-radial';

type ObjectCardMountWindow = Window & {
    fetchAndCacheObject?: (id: string, opts?: { authoredCard?: boolean }) => Promise<Record<string, unknown> | null>;
    initializeTomSelect?: () => void;
    ftRenderPlayerCardFromCache?: (id: string, target: HTMLElement, opts?: Record<string, unknown>) => boolean;
    ftRenderCreatureCardFromCache?: (id: string, target: HTMLElement, opts?: Record<string, unknown>) => boolean;
};

/** Totals and breakdowns consume the same finished instance that the object-card painter does. */
async function resolveInventoryDisplayRow(value: unknown): Promise<Record<string, unknown>> {
    const row = await compileFetchedEntity(value);
    const request = inventoryInstanceRootRequest(row);
    if (!request) return row;
    return new Promise((resolve, reject) => {
        const target = document.createElement('span');
        claimInventoryInstance(request, target, {
            node: node => { resolve(node); return false; },
            refusal: reason => { releaseInventoryInstanceClaim(target); reject(new Error(reason)); },
        });
    });
}
Object.assign(window, { ftResolveInventoryDisplayRow: resolveInventoryDisplayRow });

function renderObjectCard(
    itemArr: unknown,
    referencedNames: ReferencedNames | null | undefined,
    container: HTMLElement,
): boolean {
    if (!container) return false;
    const names = referencedNames ?? {};
    // Any render releases an earlier inventory-instance claim on this container (side panel reuse).
    releaseInventoryInstanceClaim(container);
    if (itemArr && typeof itemArr === 'object' && !Array.isArray(itemArr)) {
        const row = itemArr as Record<string, unknown>;
        if (holdPendingEntityCard(row, container, compiled => renderObjectCard(compiled, names, container))) return true;
    }
    // A saved inventory instance arrives as a SELECT shell; its card is its possessor's compiled node.
    const instance = inventoryInstanceRootRequest(itemArr);
    if (instance) {
        paintInventoryInstanceRoot(itemArr as Record<string, unknown>, instance, names, container, paintObjectCard);
        return true;
    }
    return paintObjectCard(itemArr, names, container);
}

/** Paint one card into `container` without touching its instance claim. */
function paintObjectCard(itemArr: unknown, names: ReferencedNames, container: HTMLElement): boolean {
    try {
        container.innerHTML = buildObjectCardHtml(itemArr, names);
        // Hydrate the delegated property-list sub-region from the same blob.
        const propMount = container.querySelector('.ft-oc-property-list') as HTMLElement | null;
        if (propMount && typeof window.renderItemListDisplay === 'function') {
            try { window.renderItemListDisplay(itemArr, names, propMount); } catch (e) { /* leave empty on failure */ }
        }
        // Hydrate any [table:]/[random_table:] mounts the parsed description/flavor painted (best-effort;
        // footer pass covers init). These fetch + inject server-rendered table HTML, then re-hydrate its
        // icons/runes themselves.
        if (typeof window.ftMountParseTextTables === 'function') {
            try { window.ftMountParseTextTables(container); } catch (e) { /* footer pass covers */ }
        }
        // Hydrate any inline rune chips this render painted (best-effort; footer pass covers init).
        if (typeof window.ftMountRuneIcons === 'function') {
            try { window.ftMountRuneIcons(container); } catch (e) { /* footer pass covers */ }
        }
        // Hydrate any thumbnail mounts this render painted (best-effort; footer pass covers init).
        if (typeof window.ftMountThumbnails === 'function') {
            try { window.ftMountThumbnails(container); } catch (e) { /* footer pass covers */ }
        }
        // Same for any carousel mounts this render painted (scoped, best-effort).
        if (typeof window.ftMountCarousels === 'function') {
            try { window.ftMountCarousels(container); } catch (e) { /* footer pass covers */ }
        }
        // Same for any inventory tile mounts this render painted (scoped, best-effort).
        if (typeof window.ftMountInventoryTiles === 'function') {
            try { window.ftMountInventoryTiles(container); } catch (e) { /* footer pass covers */ }
        }
        // GM container loot-generator widget: hydrate its STATIC ts-select dropdowns (scroll/tier/ammo —
        // instant, no DB) and wire the two DB-backed pool selects (skill-book/iconic-book) for on-demand
        // load on panel expand. Scoped existence check first so the common card (no selects) skips the
        // full-document initializeTomSelect scan; both calls are idempotent (:not(.tomselected) /
        // data-ft-loot-wired) so re-renders don't double-bind.
        const mountWindow = window as ObjectCardMountWindow;
        if (
            container.querySelector('select.ts-select:not(.tomselected)')
            && typeof mountWindow.initializeTomSelect === 'function'
        ) {
            try { mountWindow.initializeTomSelect(); } catch (e) { /* non-fatal */ }
        }
        try { ftWireContainerLootGen(container); } catch (e) { /* non-fatal */ }
        // Server-island fragments: fetch + slot in
        // any placeholders this render emitted. Quiet + async; the card never waits on them.
        try { ftHydrateObjectCardIslands(container, itemArr); } catch (e) { /* non-fatal */ }
        return true;
    } catch (e) {
        // A render-time global (iconMap, …) may not be defined at immediate-render time;
        // return false so the caller leaves the mount un-done for the DOMContentLoaded re-scan.
        // (The PHP-card runtime fallback was retired 2026-07-22 — a render throw now leaves the
        // mount un-done rather than fetching the archived server card.)
        console.error('renderObjectCard failed', e);
        return false;
    }
}

// ── mount scanner ────────────────────────────────────────────────────────────────
// Mirrors the list-display scanner: find .ft-oc-ts-card mounts carrying the JSON blob +
// names payloads, render client-side. JSON-in-a-script survives innerHTML (so the AJAX
// side panel works); an initial scan catches the direct item.php page load, and an
// explicit call in openItemInfo (template.js) catches AJAX-injected panels.
async function finalizeAuthoredCard(blob: Record<string, any>): Promise<Record<string, any>> {
    if (blob._entity_base?.entity === 'definition' && itemsCol(blob, 'item_type') === 'creature') {
        const row = await previewSpawnDraft(String(itemsCol(blob, 'item_id')));
        const result = { ...blob, ...row };
        delete result._entity_base;
        delete result._entity_refusal;
        return result;
    }
    return compileFetchedEntity(blob);
}

async function ftMountOne(el: Element): Promise<void> {
    if (['1', 'pending'].includes(el.getAttribute('data-oc-ts-done') ?? '')) return;
    const blobEl = el.querySelector('script.ft-oc-ts-blob');
    if (!blobEl) return;
    const namesEl = el.querySelector('script.ft-oc-ts-names');
    try {
        let blob = JSON.parse(blobEl.textContent || 'null');
        const names = namesEl ? JSON.parse(namesEl.textContent || '{}') : {};
        if (blob?._entity_base || blob?._enchantment_template_inputs) {
            if (blob._entity_base && !window.ftAdoptInventoryBase) return; // The DOM/load scan resumes after the runtime has registered.
            const id = blob._entity_base?.entity_id ?? itemsCol(blob, 'item_id');
            const context = blob._ft_object_card;
            el.setAttribute('data-oc-ts-done', 'pending');
            blob = await finalizeAuthoredCard(blob);
            if (context) blob._ft_object_card = context;
            window.FateTide ||= {}; window.FateTide.objectCache ||= new Map();
            window.FateTide.objectCache.set(id, blob);
        }
        if (!renderObjectCard(blob, { ...buildNamesMapFromBlob(blob), ...names }, el as HTMLElement)) throw new Error('authored-card:render-unavailable');
        el.setAttribute('data-oc-ts-done', '1');
    } catch (e) {
        // PHP-card runtime fallback retired 2026-07-22 — leave the mount un-done on a parse/render
        // throw (the DOMContentLoaded re-scan will retry) instead of fetching the archived server card.
        mountFailure(el as HTMLElement, e, () => { el.removeAttribute('data-oc-ts-done'); void ftMountOne(el); });
    } finally {
        if (el.getAttribute('data-oc-ts-done') === 'pending') el.removeAttribute('data-oc-ts-done');
    }
}

function mountFailure(target: HTMLElement, error: unknown, retry: () => void): void {
    const sources = Array.from(target.querySelectorAll('script[type="application/json"]')).map(node => node.cloneNode(true));
    target.replaceChildren(...sources);
    const message = document.createElement('p');
    message.textContent = error instanceof Error ? error.message : String(error ?? 'authored-card:unavailable');
    const button = document.createElement('button');
    button.type = 'button'; button.className = 'btn btn-sm btn-outline-warning'; button.textContent = 'Retry';
    button.addEventListener('click', retry, { once: true });
    target.append(message, button);
}

async function mountAuthoredAttachments(target: HTMLElement): Promise<void> {
    const host = window as ObjectCardMountWindow;
    if (target.dataset.ftAttachmentsDone || !host.fetchAndCacheObject) return;
    target.dataset.ftAttachmentsDone = 'pending';
    try {
        const root = await host.fetchAndCacheObject(target.dataset.ftAuthoredAttachments ?? '', { authoredCard: true });
        if (!root) throw new Error('authored-card:attachments-unavailable');
        const passiveOnly = target.dataset.ftPassiveSubtypes === '1';
        const children = passiveOnly ? [] : [...layerEntriesOf(root, 'creature_sub_type'), ...layerEntriesOf(root, 'creature_template')];
        if (passiveOnly && itemsCol(root, 'item_type') === 'ancestry') {
            for (const key of ['passive_ability', ...[1, 2, 3, 4].map(n => `user_choice_${n}_passive_ability`)]) {
                for (const entry of layerEntriesOf(root, key)) {
                    const id = scalarLayerSlot(entry);
                    const passive = await host.fetchAndCacheObject(id, { authoredCard: true });
                    if (!passive) throw new Error('authored-card:passive-unavailable');
                    children.push(...layerEntriesOf(passive, 'creature_sub_type'));
                }
            }
        }
        const ids = [...new Set(children.map(scalarLayerSlot).filter(Boolean))];
        const rows = await Promise.all(ids.map(async id => {
            const row = await host.fetchAndCacheObject!(id, { authoredCard: true });
            if (!row) throw new Error('authored-card:child-unavailable');
            return row;
        }));
        if (passiveOnly) rows.sort((a, b) => String(itemsCol(a, 'item_name') ?? '').localeCompare(String(itemsCol(b, 'item_name') ?? ''), undefined, { sensitivity: 'base' }));
        target.replaceChildren();
        for (const row of rows) {
            const child = document.createElement('div'); child.className = 'col-12 mb-2';
            if (!renderObjectCard(row, buildNamesMapFromBlob(row), child)) throw new Error('authored-card:child-render');
            target.append(child);
        }
        target.dataset.ftAttachmentsDone = '1';
    } catch (error) {
        delete target.dataset.ftAttachmentsDone;
        mountFailure(target, error, () => { void mountAuthoredAttachments(target); });
    }
}

async function mountAuthoredClassList(target: HTMLElement): Promise<void> {
    const host = window as ObjectCardMountWindow & { ftMountClassSummary?: () => void };
    if (target.dataset.ftClassListDone || !host.fetchAndCacheObject || !window.renderItemListDisplay) return;
    target.dataset.ftClassListDone = 'pending';
    try {
        const id = target.dataset.ftAuthoredClassList ?? '';
        const row = await host.fetchAndCacheObject(id, { authoredCard: true });
        if (!row) throw new Error('authored-card:class-unavailable');
        const painted: unknown = window.renderItemListDisplay(row, buildNamesMapFromBlob(row), target);
        if (painted === false) throw new Error('authored-card:class-render-unavailable');
        target.dataset.ftClassListDone = '1';
        // The class bundle may have scanned the SELECT shell while the worker was still cold.
        document.querySelectorAll<HTMLElement>('[data-ft-class-id]').forEach(el => {
            if (el.dataset.ftClassId === id) el.removeAttribute('data-ft-cs-done');
        });
        host.ftMountClassSummary?.();
    } catch (error) {
        delete target.dataset.ftClassListDone;
        mountFailure(target, error, () => { void mountAuthoredClassList(target); });
    }
}

function ftMountObjectCards(): void {
    hydrateEncounterLinkZones(document);
    document.querySelectorAll<HTMLElement>('[data-ft-spawn-preview-base]:not([data-ft-spawn-preview-mounted])').forEach(mount => {
        mount.dataset.ftSpawnPreviewMounted = '1';
        void previewSpawnDraft(mount.dataset.ftSpawnPreviewBase ?? '', {}, mount.dataset.ftSpawnPreviewTemplate || null)
            .then(source => { if (!renderObjectCard(source, {}, mount)) throw new Error('spawn:preview-render-failed'); })
            .catch(error => { mount.textContent = String(error instanceof Error ? error.message : error); });
    });
    document.querySelectorAll('.ft-oc-ts-card:not([data-oc-ts-done="1"])').forEach(ftMountOne);
    document.querySelectorAll<HTMLElement>('[data-ft-authored-class-list]').forEach(target => { void mountAuthoredClassList(target); });
    document.querySelectorAll<HTMLElement>('[data-ft-authored-attachments]').forEach(target => { void mountAuthoredAttachments(target); });
}

async function ftMountCasterRouteCard(el: Element): Promise<void> {
    if (['1', 'pending'].includes(el.getAttribute('data-ft-caster-done') ?? '')) return;
    const target = el as HTMLElement;
    const blobEl = target.querySelector('.ft-caster-route-blob');
    const id = target.dataset.ftCasterId || '';
    const kind = target.dataset.ftCasterKind || '';
    if (!blobEl || !id) return;
    try {
        let blob = JSON.parse(blobEl.textContent || 'null');
        window.FateTide ||= {};
        window.FateTide.casterCache ||= new Map();
        if (blob?._entity_base) {
            if (!window.ftAdoptInventoryBase) return;
            el.setAttribute('data-ft-caster-done', 'pending');
            blob = await finalizeAuthoredCard(blob);
            window.FateTide.casterCache.set(id, blob);
        } else window.FateTide.casterCache.set(id, blob);
        const host = window as ObjectCardMountWindow;
        const rendered = kind === 'player'
            ? host.ftRenderPlayerCardFromCache?.(id, target, { sideInfo: false })
            : host.ftRenderCreatureCardFromCache?.(id, target, {
                sideInfo: false,
                viewTarget: id,
                // item.php's route mount owns the Bootstrap half-width column so the
                // rendered card must fill that mount, not apply a second col-lg-6.
                cardColClasses: target.classList.contains('col-lg-6') ? 'col-12 h-100' : undefined,
            });
        if (rendered) target.setAttribute('data-ft-caster-done', '1');
    } catch (error) {
        mountFailure(target, error, () => { el.removeAttribute('data-ft-caster-done'); void ftMountCasterRouteCard(el); });
    } finally {
        if (el.getAttribute('data-ft-caster-done') === 'pending') el.removeAttribute('data-ft-caster-done');
    }
}

function ftMountCasterRouteCards(): void {
    document.querySelectorAll('.ft-caster-route-card:not([data-ft-caster-done="1"])').forEach(ftMountCasterRouteCard);
}

function ftMountItemRouteCacheSeed(): void {
    if (typeof document.getElementById !== 'function') return;
    const el = document.getElementById('ft-item-route-cache-seed');
    if (!el || el.getAttribute('data-ft-seed-done') === '1') return;
    try {
        const payload = JSON.parse(el.textContent || '{}') as Record<string, { id?: unknown; value?: unknown } | null>;
        window.FateTide ||= {};
        const mappings: Array<[string, 'casterCache' | 'campaignCache' | 'encounterCache' | 'objectCache']> = [
            ['caster', 'casterCache'], ['campaign', 'campaignCache'], ['encounter', 'encounterCache'], ['object', 'objectCache'],
        ];
        for (const [key, cacheName] of mappings) {
            const entry = payload[key];
            if (!entry || typeof entry.id !== 'string' || !entry.value) continue;
            const cacheHost = window.FateTide as unknown as Record<string, unknown>;
            let cache = cacheHost[cacheName];
            if (!(cache instanceof Map)) {
                cache = new Map<string, unknown>();
                cacheHost[cacheName] = cache;
            }
            (cache as Map<string, unknown>).set(entry.id, entry.value);
        }
        el.setAttribute('data-ft-seed-done', '1');
    } catch (error) {
        console.error('[item-route-cache] invalid seed', error);
    }
}

// Idempotent scan for .ft-caster-cache-seed JSON mounts (ft_emit_caster_cache_seed's non-executable
// twin). Needed because inline <script> injected via innerHTML (queryData → the SideInfo server-
// fallback creature card) never executes — this scanner, re-invoked from loadPostFunctions after
// every AJAX inject, is how those paths seed window.FateTide.casterCache (so cache-only nodes like a
// static creature's synthetic enchanted equipped gear resolve on click instead of 404ing).
function ftMountCasterCacheSeeds(): void {
    document.querySelectorAll('script.ft-caster-cache-seed:not([data-ft-seed-done="1"])').forEach((el) => {
        const id = el.getAttribute('data-ft-caster-id') || '';
        if (!id) { el.setAttribute('data-ft-seed-done', '1'); return; }
        try {
            const blob = JSON.parse(el.textContent || 'null');
            if (blob && typeof blob === 'object') {
                window.FateTide ||= {};
                window.FateTide.casterCache ||= new Map();
                window.FateTide.casterCache.set(id, blob);
            }
            el.setAttribute('data-ft-seed-done', '1');
        } catch (error) {
            console.error('[caster-cache-seed] invalid seed', error);
            el.setAttribute('data-ft-seed-done', '1'); // don't re-parse a broken payload forever
        }
    });
}

Object.assign(window, { renderObjectCard, ftMountObjectCards, ftMountCasterRouteCards, ftMountCasterCacheSeeds, ftPreviewSpawnDraft: previewSpawnDraft });

// Render any page-load mount once the DOM is ready (one-time querySelectorAll; no-op on
// pages with no mount). AJAX side panels are handled by an explicit ftMountObjectCards()
// call in openItemInfo (template.js) after the panel HTML lands.
(function ftInitOcTs(): void {
    ftMountItemRouteCacheSeed();
    ftMountCasterCacheSeeds();
    ftMountObjectCards();
    ftMountCasterRouteCards();
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            ftMountObjectCards();
            ftMountCasterRouteCards();
            window.setTimeout(ftMountCasterRouteCards, 250);
            window.setTimeout(ftMountCasterRouteCards, 1000);
        });
        window.addEventListener('load', ftMountCasterRouteCards, { once: true });
    }
})();
